<script>
import constants from '@/constants';
import { axiosRequest, METHOD } from '@/api/default.js';
import {
    getUserProviderAssignments,
    assignProviderToUser,
    removeProviderFromUser,
} from '@/api/publisherAdmin.js';

export default {
    name: 'UserEditPublisherAssignment',

    data() {
        return {
            // Current assignments
            assignments: [],
            isLoading: false,

            // Provider search for new assignment
            providerSearch: '',
            providerSearchTimer: null,
            providerResults: [],
            providerSearchLoading: false,
            selectedProvider: null,
            selectedRole: 'member',

            // Action state
            isAssigning: false,
            removingProviderId: null,

            // Public visibility for new assignment
            isPublic: true,

            errorText: '',
        };
    },

    computed: {
        userId() {
            return this.$store.getters.USER_EDIT_DATA?.id || null;
        },
        canAssign() {
            return this.selectedProvider !== null && !this.isAssigning;
        },
    },

    watch: {
        userId: {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.loadAssignments(newId);
                } else {
                    this.assignments = [];
                }
            },
        },
    },

    methods: {
        // ── Load ──────────────────────────────────────────────────────────────
        async loadAssignments(userId) {
            this.isLoading = true;
            this.errorText = '';
            try {
                this.assignments = await getUserProviderAssignments(userId);
                if (process.env.NODE_ENV !== 'production') {
                    console.info(
                        '[UserEditPublisherAssignment.loadAssignments]',
                        { userId, count: this.assignments.length }
                    );
                }
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[UserEditPublisherAssignment.loadAssignments] Error:',
                        err?.message
                    );
                }
                this.errorText = 'Failed to load assignments.';
                this.assignments = [];
            } finally {
                this.isLoading = false;
            }
        },

        // ── Provider search ───────────────────────────────────────────────────
        handleProviderSearchInput() {
            clearTimeout(this.providerSearchTimer);
            this.selectedProvider = null;
            if (!this.providerSearch.trim()) {
                this.providerResults = [];
                return;
            }
            this.providerSearchTimer = setTimeout(() => {
                this.searchProviders(this.providerSearch.trim());
            }, 300);
        },

        async searchProviders(query) {
            this.providerSearchLoading = true;
            try {
                const results = await axiosRequest({
                    url: constants.URL.PROVIDER_GET,
                    method: METHOD.GET,
                    requestName: 'GET PROVIDER SEARCH FOR ASSIGNMENT',
                    isLoader: false,
                    params: { search: query, get_all: true },
                });
                const raw = Array.isArray(results)
                    ? results
                    : results?.items || [];
                // Exclude providers already assigned
                const assignedIds = new Set(
                    this.assignments.map((a) => a.provider_id)
                );
                this.providerResults = raw.filter(
                    (p) => !assignedIds.has(p.id)
                );
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[UserEditPublisherAssignment.searchProviders] Error:',
                        err?.message
                    );
                }
                this.providerResults = [];
            } finally {
                this.providerSearchLoading = false;
            }
        },

        selectProvider(provider) {
            this.selectedProvider = provider;
            this.providerSearch = provider.name || String(provider.id);
            this.providerResults = [];
        },

        clearProviderSelection() {
            this.selectedProvider = null;
            this.providerSearch = '';
            this.providerResults = [];
        },

        // ── Assign ────────────────────────────────────────────────────────────
        async handleAssign() {
            if (!this.canAssign || !this.userId) return;
            this.isAssigning = true;
            this.errorText = '';
            try {
                const assignment = await assignProviderToUser(this.userId, {
                    provider_id: this.selectedProvider.id,
                    role: this.selectedRole,
                    is_public: this.isPublic,
                });
                if (process.env.NODE_ENV !== 'production') {
                    console.info(
                        '[UserEditPublisherAssignment.handleAssign] assigned:',
                        { userId: this.userId, providerId: this.selectedProvider.id }
                    );
                }
                this.assignments.push(assignment);
                this.clearProviderSelection();
                this.selectedRole = 'member';
                this.isPublic = true;
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[UserEditPublisherAssignment.handleAssign] Error:',
                        err?.message
                    );
                }
                this.errorText = 'Failed to assign provider.';
            } finally {
                this.isAssigning = false;
            }
        },

        // ── Remove ────────────────────────────────────────────────────────────
        async handleRemove(assignment) {
            if (this.removingProviderId !== null || !this.userId) return;
            this.removingProviderId = assignment.provider_id;
            this.errorText = '';
            try {
                await removeProviderFromUser(this.userId, assignment.provider_id);
                if (process.env.NODE_ENV !== 'production') {
                    console.info(
                        '[UserEditPublisherAssignment.handleRemove] removed:',
                        { userId: this.userId, providerId: assignment.provider_id }
                    );
                }
                this.assignments = this.assignments.filter(
                    (a) => a.provider_id !== assignment.provider_id
                );
            } catch (err) {
                if (process.env.NODE_ENV !== 'production') {
                    console.error(
                        '[UserEditPublisherAssignment.handleRemove] Error:',
                        err?.message
                    );
                }
                this.errorText = 'Failed to remove assignment.';
            } finally {
                this.removingProviderId = null;
            }
        },
    },
};
</script>

<template>
    <div class="left_sub_panel_data">
        <!-- Loading state -->
        <div v-if="isLoading" class="mt_5 mb_10 text_color">
            Loading assignments...
        </div>

        <!-- Current assignments list -->
        <div v-else>
            <div v-if="assignments.length === 0" class="mt_5 mb_10 text_color">
                No provider assignments.
            </div>

            <div
                v-for="assignment in assignments"
                :key="assignment.provider_id"
                class="assignment_row"
            >
                <span class="assignment_name">{{ assignment.provider_name }}</span>
                <span
                    class="assignment_role"
                    :class="assignment.role === 'owner' ? 'green_color' : 'text_color'"
                >
                    {{ assignment.role }}
                </span>
                <span
                    class="assignment_visibility"
                    :class="assignment.is_public ? 'badge_public' : 'badge_hidden'"
                    title="Portal visibility"
                >
                    {{ assignment.is_public ? 'Public' : 'Hidden' }}
                </span>
                <button
                    class="button_red_small ml_10"
                    :disabled="removingProviderId === assignment.provider_id"
                    @click="handleRemove(assignment)"
                    title="Remove assignment"
                >
                    {{
                        removingProviderId === assignment.provider_id
                            ? '...'
                            : 'Remove'
                    }}
                </button>
            </div>
        </div>

        <!-- Error -->
        <label v-if="errorText" class="error_text mt_5">{{ errorText }}</label>

        <!-- Divider -->
        <hr class="hr_light_98 mt_10 mb_10" />

        <!-- Add new assignment — only shown when user has no existing assignment -->
        <template v-if="assignments.length === 0">
            <label class="mb_5">Assign provider:</label>

            <!-- Provider search input -->
            <div class="mb_5">
                <input
                    v-model="providerSearch"
                    type="text"
                    class="right_input"
                    placeholder="Search provider by name..."
                    @input="handleProviderSearchInput"
                    autocomplete="off"
                />

                <!-- Clear badge when provider selected -->
                <div v-if="selectedProvider" class="selected_provider_badge mt_5">
                    <span>{{ selectedProvider.name }}</span>
                    <button
                        class="close_btn_small ml_5"
                        @click="clearProviderSelection"
                        title="Clear"
                    >
                        ×
                    </button>
                </div>

                <!-- Inline results list -->
                <div
                    v-else-if="providerResults.length > 0"
                    class="search_dropdown"
                >
                    <div
                        v-for="provider in providerResults"
                        :key="provider.id"
                        class="search_dropdown_item"
                        @click="selectProvider(provider)"
                    >
                        {{ provider.name }}
                    </div>
                </div>
                <div
                    v-else-if="providerSearchLoading"
                    class="search_dropdown"
                >
                    <div class="search_dropdown_item text_color">Searching...</div>
                </div>
            </div>

            <!-- Role selector -->
            <div class="left_sub_multi_container mb_5">
                <select v-model="selectedRole" class="right_input">
                    <option value="member">Member</option>
                    <option value="owner">Owner</option>
                </select>
            </div>

            <!-- Public toggle -->
            <div class="left_sub_multi_container mb_10">
                <label class="checkbox_label">
                    <input
                        v-model="isPublic"
                        type="checkbox"
                        class="mr_5"
                    />
                    Public
                    <span class="text_color ml_5" style="font-size:11px;">
                        (uncheck to hide this provider's materials from the main portal)
                    </span>
                </label>
            </div>

            <button
                class="button_blue_100"
                :disabled="!canAssign"
                @click="handleAssign"
            >
                {{ isAssigning ? 'Assigning...' : 'Assign' }}
            </button>
        </template>

        <!-- Info when user already has an assignment -->
        <div v-else class="mt_5 text_color" style="font-size:12px;">
            User already has a provider assignment. Remove it first to assign a
            different one.
        </div>
    </div>
</template>

<style scoped>
.assignment_row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 4px;
}

.assignment_name {
    flex: 1;
    font-size: 13px;
}

.assignment_role {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.assignment_visibility {
    font-size: 11px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.badge_public {
    background: rgba(16, 185, 129, 0.2);
    color: #6ee7b7;
    border: 1px solid #065f46;
}

.badge_hidden {
    background: rgba(100, 100, 100, 0.2);
    color: #9ca3af;
    border: 1px solid #4b5563;
}

.checkbox_label {
    display: flex;
    align-items: center;
    font-size: 13px;
    cursor: pointer;
}

.button_red_small {
    background: #7f1d1d;
    color: #fca5a5;
    border: 1px solid #991b1b;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
}
.button_red_small:hover:not(:disabled) {
    background: #991b1b;
}
.button_red_small:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.search_dropdown {
    position: static;
    background: #2a1f1f;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    margin-top: 4px;
    max-height: 160px;
    overflow-y: auto;
}

.search_dropdown_item {
    padding: 6px 10px;
    cursor: pointer;
    font-size: 13px;
}
.search_dropdown_item:hover {
    background: rgba(255, 255, 255, 0.08);
}

.selected_provider_badge {
    display: inline-flex;
    align-items: center;
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid #065f46;
    border-radius: 12px;
    padding: 2px 10px;
    font-size: 12px;
    color: #6ee7b7;
}

.close_btn_small {
    background: none;
    border: none;
    color: #6ee7b7;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 0;
}
</style>
